import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import {

  Campaign,
  ChevronLeft,
  ChevronRight,

  HelpOutline,
  Home,

  Menu as MenuIcon,

  ShoppingBag,

  Store,
  Task,
  Collections,

  Close,
  NavigateNext,
} from "@mui/icons-material";
import {
  Box,
  CssBaseline,
  Toolbar,
  IconButton,
  Typography,
  Drawer as MuiDrawer,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useTheme,
  CSSObject,
  Theme,
  Button,
  Tooltip,
  ListItemAvatar,
  Dialog,
  DialogContent,
  Slide,
  Card,
  CardContent,
  Breadcrumbs,
  FormControlLabel,
  Grid,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import React, { ReactElement, useEffect, useState } from "react";
import { loginRequest } from "auth/authConfig";
import { ReactComponent as Logo } from "assets/s2p_logo.svg";
import { PUIAvatar } from "modules/UI/components/Avatar.component";
import { NavLink, Outlet, useMatch, useNavigate } from "react-router-dom";
import { useActiveUser } from "modules/User/activeUser";
import { IUserRole } from "modules/User/service/user";
import ErrorBoundary from "modules/UI/components/ErrorBoundary.component";
import EasyRequestCart from "modules/EasyRequest/components/EasyRequestCart.component";
import UserSettingsDrawer from "modules/User/components/UserSettingsDrawer.component";
import { TransitionProps } from "@mui/material/transitions";
import { useCompare } from "modules/PromoItems/containers/compare.container";
import { PUIContentCarousel } from "modules/UI/components/ContentCarousel.component";
import { BarcodeButton } from "modules/UI/components/BarcodeButton.component";
import moment from "moment";
import StaticResources from "service/dummyData/resources";
import { useFeatures } from "modules/FeatureManagement/service/featureManagement.hooks";
import {
  CartButton,
  CartDrawer,
} from "modules/PromoItems/containers/cart.container";

export type NavigationMenuItem = {
  name?: string;
  uri?: string;
  icon?: ReactElement;
  badgeContent?: number;
  feature?: string;
  dataCy?: string;
  hidden?: boolean;
  tourId?: string;
  isComingSoon?: boolean;
  isExternal?: boolean;
  customTag?: string;
  isSeparator?: boolean;
};

const drawerWidth = 240;

const openedMixin = (theme: Theme): CSSObject => ({
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});
const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  color: theme.palette.primary.contrastText,
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));
interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  background: theme.palette.background.paper,
  color: theme.palette.text.primary,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const Layout = (props) => {
  const theme = useTheme();
  const features = useFeatures();
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const activeUser = useActiveUser();
  const { selectedMaterials } = useCompare();
  const [open, setOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [compareOpen, setCompareOpen] = React.useState(false);
  const [cartOpen, setCartOpen] = React.useState(false);
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [showEmptyFields, setShowEmptyFields] = useState<boolean>(false);

  const isSupplierUser = activeUser.getUserRole() === IUserRole.SUPPLIER;

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const navItems: NavigationMenuItem[] = [
    {
      name: "Home",
      uri: "/",
      icon: <Home />,
    },
    {
      name: "Browse",
      uri: "/browse",
      icon: <ShoppingBag />,
    },
    {
      name: "Manage",
      uri: "/manage",
      icon: <Store />,
    },
    // {
    //   name: "Briefs",
    //   uri: "/briefs",
    //   isComingSoon: true,
    //   icon: <Handshake />,
    // },
    {
      name: "Campaigns",
      uri: "/campaigns",
      isComingSoon: false,
      icon: <Campaign />,
    },
  ];

  if (activeUser.isPPAAboveMarketUser()) {
    navItems.push({
      name: "Taxonomy",
      uri: "/admin/taxonomy",
      icon: <Collections />,
    });
  }

  const filteredNavItems = !activeUser.isAtMarket()
  ? navItems.filter((item) => item.name !== "Manage")
  : navItems;

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <UserSettingsDrawer
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      />
      <CartDrawer
        open={cartOpen}
        onClose={() => {
          setCartOpen(false);
        }}
      />
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', textAlign: 'center' }}>
            Explore the NSC processes!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            Please select a guidance section to continue
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            {!isSupplierUser && (
              <Button
                variant="contained"
                color="secondary"
                size="medium"
                href="https://nestle3.mcoutput.com/s2p-all-content/4_search-order/nsc/nsc.htm?Highlight=nsc"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: "none", width: 300, maxWidth: "100%" }}
              >
                S2P Help - NSC
              </Button>
            )}
            <Button
              variant="contained"
              color="secondary"
              size="medium"
              component="a"
              href="https://digital-leap.nestle.com/nsp/13-supplier-training-center/nsc/nsc.htm?tocpath=Nestle%20Supplier%20Catalogs%7c_____0"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: "none", width: 300, maxWidth: "100%" }}
            >
              Nestlé Supplier Training Center - NSC
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog
        fullScreen
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        TransitionComponent={Transition}
      >
        <AppBar color="primary" sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setCompareOpen(false)}
              aria-label="close"
            >
              <Close />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Compare Materials
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showEmptyFields}
                  onChange={() => setShowEmptyFields(!showEmptyFields)}
                />
              }
              label="Show Empty Fields"
            />
          </Toolbar>
        </AppBar>
        <Box
          display="flex"
          flexDirection={"row"}
          alignItems={"flex-start"}
          sx={{ p: 3, height: "100%", overflow: "auto" }}
        >
          {selectedMaterials.map((material) => (
            <Card sx={{ width: 380, flexShrink: 0, mx: 1, minHeight: "100%" }}>
              {/* <CardHeader titl e="Product title" /> */}
              <CardContent>
                <Box sx={{ mt: -2, mx: -2 }}>
                  <PUIContentCarousel
                    data={material?.mediaFiles
                      .filter((file) => !file.isQuality)
                      .map((file) => ({
                        type: "image",
                        thumbnail: file.url,
                        src: file.url,
                      }))}
                  />
                </Box>
                <Breadcrumbs
                  sx={{ my: 2 }}
                  separator={<NavigateNext fontSize="small" />}
                  aria-label="breadcrumb"
                >
                  {[
                    <NavLink
                      key="1"
                      color="inherit"
                      to="/browse"
                      onClick={() => {}}
                    >
                      Browse
                    </NavLink>,
                    <NavLink
                      key="1"
                      color="inherit"
                      to={`/browse?catId=${material?.promoCategoryId}`}
                      onClick={() => {}}
                    >
                      {
                        StaticResources.promoCategories.find(
                          (cat) => cat.id == material?.promoCategoryId
                        )?.name
                      }
                    </NavLink>,
                  ]}
                </Breadcrumbs>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                  {material?.shortDescription}
                  {/* [{material?.materialId}] {material?.materialName} */}
                </Typography>
                <Typography variant="body1">
                  {material?.supplierName}
                </Typography>
                <Typography variant="body2">{material?.description}</Typography>
                <Grid container spacing={2} sx={{ py: 6 }}>
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      <Table>
                        <TableBody>
                          {[
                            {
                              name: "Catalogue ID",
                              value: material?.pslIdWithPrefix,
                            },
                            {
                              name: "Order Window ID",
                              value: material?.orderWindowIdWithPrefix,
                            },
                            {
                              name: "Supplier Part Id",
                              value: !material?.supplierPartNumber ? null : (
                                <>
                                  {material?.supplierPartNumber}{" "}
                                  <BarcodeButton
                                    value={material?.supplierPartNumber}
                                  />
                                </>
                              ),
                            },
                            {
                              name: "Manufacturer Part Number",
                              value:
                                !material?.manufacturerPartNumber ? null : (
                                  <>
                                    {material?.manufacturerPartNumber}{" "}
                                    <BarcodeButton
                                      value={material?.manufacturerPartNumber}
                                    />
                                  </>
                                ),
                            },
                            {
                              name: "Status",
                              value: material?.statusName,
                            },
                            {
                              name: "MOQ",
                              value: material?.minimumOrderQuantity,
                            },
                            {
                              name: "MPQ",
                              value: material?.minimumPurchaseQuantity,
                            },
                            {
                              name: "IncoTerms",
                              value: material?.incoTerms,
                            },
                            {
                              name: "Quantity",
                              value: (
                                <>
                                  {/* <TextField
                                    value={quantity}
                                    type={"number"}
                                    variant={"standard"}
                                    onChange={(e) =>
                                      setQuantity(e.target.value)
                                    }
                                    error={showQuantityError}
                                    helperText={
                                      showQuantityError
                                        ? "Quantity must be greater than MOQ and multiple of MPQ"
                                        : ""
                                    }
                                    inputProps={{
                                      step: material?.minimumPurchaseQuantity,
                                    }}
                                  /> */}
                                </>
                              ),
                            },
                            // {
                            //   name: "Unit Price",
                            //   value: unitPrice,
                            // },
                            {
                              name: "Currency",
                              value: material?.currencyCode,
                            },
                            // {
                            //   name: "Price Per",
                            //   value: pricePer,
                            // },
                            {
                              name: "Unit of measure",
                              value: material?.unitOfMeasure,
                            },
                            {
                              name: "Valid From",
                              value: moment(material?.validFrom).format(
                                "DD/MM/YYYY"
                              ),
                            },
                            {
                              name: "Valid To",
                              value: moment(material?.validTo).format(
                                "DD/MM/YYYY"
                              ),
                            },
                            {
                              name: "Agreements",
                              value: material?.agreements,
                            },
                            {
                              name: "Strategic Buyer",
                              value: material?.strategicBuyerEmailAddress,
                            },
                            {
                              name: "Created By",
                              value: material?.createdByUser?.email,
                            },
                            {
                              name: "Created On",
                              value: moment(material?.createdOn).format(
                                "DD/MM/YYYY"
                              ),
                            },
                            {
                              name: "Modified By",
                              value: material?.modifiedByUser?.email,
                            },
                            {
                              name: "Modified On",
                              value: moment(material?.modifiedOn).format(
                                "DD/MM/YYYY"
                              ),
                            },
                          ]
                            .filter((field) => {
                              return !!field.value || showEmptyFields;
                            })
                            .map((field) => (
                              <TableRow key={field.name}>
                                <TableCell>
                                  <strong>{field.name}</strong>
                                </TableCell>
                                <TableCell>{field.value}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Dialog>
      <StyledDrawer variant="permanent" open={open}>
        <DrawerHeader>
          {/* <img src={logo} style={{ marginRight: "auto" }} /> */}
          <Logo />
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {filteredNavItems.map((item, index) =>
            item.isSeparator ? (
              <Divider key={"divider" + index} />
            ) : (
              <Tooltip
                title={`${item.isComingSoon ? " Coming Soon: " : ""}${
                  item.name
                }`}
                placement="right"
                arrow
              >
                <ListItem
                  key={item.name + item.uri}
                  disablePadding
                  sx={{ display: "block" }}
                >
                  <ListItemButton
                    disabled={item.isComingSoon}
                    component={NavLink}
                    to={item.uri}
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? "initial" : "center",
                      px: 2.5,
                      "&:hover, &.Mui-selected, &.active": {
                        backgroundColor:
                          theme.palette.primary.light + " !important",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: "inherit",
                        minWidth: 0,
                        mr: open ? 3 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      sx={{ opacity: open ? 1 : 0 }}
                    />
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            )
          )}
        </List>
      </StyledDrawer>
      <AppBar position="fixed" elevation={0} open={open}>
        <Toolbar sx={{ gap: theme.spacing(2), borderBottom: "1px solid #ddd" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 2,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              marginRight: "auto",
            }}
          >
            <Typography variant="h6" noWrap>
              Supplier Catalogs
            </Typography>
            <Tooltip title="NSC Help" arrow>
              <IconButton onClick={() => setHelpOpen(true)}>
                <HelpOutline />
              </IconButton>
            </Tooltip>
          </Box>

          {isAuthenticated ? (
            <>
              <Divider orientation="vertical" flexItem sx={{ ml: "auto" }} />
              <Tooltip title="MyTasks" arrow>
                <IconButton
                  onClick={(e) => {
                    navigate("/tasks");
                  }}
                >
                  {/* <Badge badgeContent={4} color="secondary"> */}
                  <Task />
                  {/* </Badge> */}
                </IconButton>
              </Tooltip>
              {/* {features["enableV2"] && (
                <>
                  <Divider orientation="vertical" flexItem />
                  <Tooltip title="Compare Materials" arrow>
                    <IconButton onClick={() => setCompareOpen(true)}>
                      <Badge
                        badgeContent={selectedMaterials.length}
                        color="secondary"
                      >
                        <CompareArrowsRounded />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                </>
              )} */}
              <Divider orientation="vertical" flexItem />
              {features["enableV2"] ? (
                <>
                  <CartButton
                    onClick={() => {
                      setCartOpen(true);
                    }}
                  />
                  <EasyRequestCart />
                </>
              ) : (
                <EasyRequestCart />
              )}
              <Divider orientation="vertical" flexItem />
              <ListItemButton
                dense
                sx={{ width: "auto", flexGrow: 0 }}
                onClick={(e) => setProfileOpen(true)}
              >
                <ListItemAvatar>
                  <PUIAvatar src={activeUser.photoUrl} />
                </ListItemAvatar>
                <ListItemText
                  primary={activeUser.name}
                  secondary={activeUser.userType}
                />
              </ListItemButton>
            </>
          ) : (
            <Button
              sx={{ marginLeft: "auto" }}
              variant="contained"
              color="primary"
              onClick={() => {
                instance
                  .loginRedirect(loginRequest)
                  .catch((e) => console.error(e));
              }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          maxWidth: `calc(100% - ${open ? drawerWidth : 65}px)`,
          minHeight: "100vh",
        }}
      >
        <DrawerHeader />{" "}
        {/** this guy is pushing down the content to prevent the app bar from hiding it */}
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
};
